import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { ToasterService } from '@app/shared/services/toaster.service';
import { MatTableModule } from '@angular/material/table';
import {
  AddOneTimeEntryRequest,
  AddResourceProjectRequest,
  EditResourceProjectSection,
  EditResourceProjectView,
  FinanceProjectOption,
  FinanceService,
  OneTimeEntryView,
  ReferenceEntry,
  ResourceHistoryEntry,
  TierRateRow,
} from '../../finance.service';

@Component({
  selector: 'app-finance-edit-project',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatButtonModule,
    MatCardModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatNativeDateModule,
    MatSelectModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
  ],
  templateUrl: './finance-edit-project.component.html',
  styleUrl: './finance-edit-project.component.scss',
})
export class FinanceEditProjectComponent implements OnInit, OnDestroy {
  /** Canonical tier values seeded into the Tier Model dropdown on the
   *  Current + New Project forms. tier_model on the entity is free-text
   *  so operators can still type a value outside this list if they need. */
  readonly tierOptions: readonly string[] = [
    'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4',
    'Actuals', 'Vendor Billing', 'Non-Billable',
  ];

  /** Canonical Laptop Issued values. Operators picking a non-listed
   *  brand/model would need to extend this list — net-new historical
   *  values from imports stay as-is on the rate card but render in the
   *  trigger area only when they happen to match one of these. */
  readonly laptopIssuedOptions: readonly string[] = [
    'Dell', 'Lenovo', 'MacBook', 'MacBook Pro', 'VDI', 'None',
  ];

  /** Master rates by tier_model for the current year, loaded once on
   *  init. Picking a Tier Model on either form looks this up to auto-fill
   *  Base Rate. Missing master entries leave the form's Base Rate alone
   *  so operators can still type a value manually. */
  private readonly tierRateMaster = new Map<string, TierRateRow>();

  resourceId!: number;
  /** Pulled from the ?name= query param on navigation — used for the
   *  page header. The BE doesn't have direct access to Employee.name. */
  resourceName = '';
  employeeId = '';
  /** Captured from the BE view payload so the Tier Model change
   *  handler can gate Base Rate auto-fill on POI Type = "EMP". */
  poiType: string | null = null;

  /** The FormArray of project sections. One section per active assignment. */
  editForm!: FormGroup;
  /** Standalone form for the New Project tab — fresh assignment + optional
   *  rate card. Single FormGroup, not an array (one new at a time). */
  newProjectForm!: FormGroup;
  /** Standalone form for the One Time tab — adds a one-off monetary
   *  adjustment to a resource for a specific month / year. */
  oneTimeForm!: FormGroup;

  /** List of one-time entries already posted for this resource. Refreshed
   *  on tab open and after a successful add. */
  oneTimeEntries: OneTimeEntryView[] = [];
  readonly oneTimeColumns: string[] = ['period', 'amount', 'comments', 'createdBy', 'createdAt'];
  oneTimeLoading = false;
  addingOneTime = false;


  /** Month dropdown options for the One Time form. */
  readonly monthOptions: { value: number; label: string }[] = [
    { value: 1, label: 'Jan' },
    { value: 2, label: 'Feb' },
    { value: 3, label: 'Mar' },
    { value: 4, label: 'Apr' },
    { value: 5, label: 'May' },
    { value: 6, label: 'Jun' },
    { value: 7, label: 'Jul' },
    { value: 8, label: 'Aug' },
    { value: 9, label: 'Sep' },
    { value: 10, label: 'Oct' },
    { value: 11, label: 'Nov' },
    { value: 12, label: 'Dec' },
  ];

  loading = false;
  saving = false;
  addingProject = false;
  errorMessage: string | null = null;

  /** Selected tab on the main card. 0 = Current Project (active),
   *  1 = New Project (stub), 2 = One Time (stub). PR 2/3 builds out
   *  the stubbed tabs. */
  activeMainTab = 0;

  /**
   * Indices of currently-expanded project sections. Project 1 (index 0)
   * is expanded by default; later sections start collapsed so the
   * Save / Cancel button bar stays reachable without scrolling.
   */
  expandedSections = new Set<number>([0]);

  isExpanded(index: number): boolean {
    return this.expandedSections.has(index);
  }

  toggleSection(index: number): void {
    if (this.expandedSections.has(index)) {
      this.expandedSections.delete(index);
    } else {
      this.expandedSections.add(index);
    }
  }

  hiringStatusOptions: ReferenceEntry[] = [];
  capexOpexOptions: ReferenceEntry[] = [];
  projectOptions: FinanceProjectOption[] = [];
  /** Indexed by project id for O(1) lookup on picker change. */
  private projectsById = new Map<number, FinanceProjectOption>();

  /** Right-panel data — populated lazily when the user clicks the side
   *  panel tabs (or eagerly on init; both endpoints are cheap for one
   *  resource). */
  projectHistory: ResourceHistoryEntry[] = [];
  recentActivities: ResourceHistoryEntry[] = [];
  rightPanelLoading = false;

  private readonly subs = new Subscription();

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly financeService: FinanceService,
    private readonly toaster: ToasterService,
  ) {
    this.editForm = this.fb.group({
      projects: this.fb.array([]),
    });
    this.newProjectForm = this.buildNewProjectForm();
    this.oneTimeForm = this.buildOneTimeForm();
  }

  private buildOneTimeForm(): FormGroup {
    const currentYear = 2026; // Date.now is sandboxed in this runtime; defaulting to current finance year — operator can change.
    return this.fb.group({
      targetYear: [currentYear, [Validators.required, Validators.min(2000), Validators.max(2100)]],
      targetMonth: [null as number | null, Validators.required],
      amount: [null as number | null, Validators.required],
      comments: ['', Validators.required],
    });
  }

  monthLabel(month: number | undefined | null): string {
    if (!month) return '';
    const found = this.monthOptions.find((m) => m.value === month);
    return found ? found.label : String(month);
  }

  formatCurrency(value: number | undefined | null): string {
    if (value === null || value === undefined) return '';
    const abs = Math.abs(Number(value));
    const sign = Number(value) < 0 ? '-' : '';
    return `${sign}$${abs.toFixed(2)}`;
  }

  formatTimestamp(iso: string | undefined | null): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(2);
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${mi}`;
  }

  /** Dispatched by mat-tab-group on every tab change (mouse OR keyboard).
   *  Lazy-loads the One Time list on first focus so the GET only fires
   *  when the operator actually needs it. */
  onMainTabChange(index: number): void {
    this.activeMainTab = index;
    if (index === 2 && this.oneTimeEntries.length === 0 && !this.oneTimeLoading) {
      this.loadOneTimeEntries();
    }
  }

  private loadOneTimeEntries(): void {
    this.oneTimeLoading = true;
    this.subs.add(
      this.financeService.listOneTimeEntries(this.resourceId).subscribe({
        next: (rows) => {
          this.oneTimeEntries = rows;
          this.oneTimeLoading = false;
        },
        error: (err) => {
          console.error('One-time entries load failed', err);
          this.oneTimeEntries = [];
          this.oneTimeLoading = false;
        },
      }),
    );
  }

  addOneTimeEntry(): void {
    if (this.oneTimeForm.invalid) {
      this.oneTimeForm.markAllAsTouched();
      this.toaster.showError('Please fix the highlighted fields and try again', 'Validation error');
      return;
    }
    this.addingOneTime = true;
    const v = this.oneTimeForm.value;
    const payload: AddOneTimeEntryRequest = {
      targetYear: v.targetYear,
      targetMonth: v.targetMonth,
      amount: v.amount,
      comments: (v.comments ?? '').trim(),
    };
    this.subs.add(
      this.financeService.addOneTimeEntry(this.resourceId, payload).subscribe({
        next: () => {
          this.addingOneTime = false;
          this.toaster.showSuccess('One-time entry added', 'Success');
          this.oneTimeForm.reset({
            targetYear: payload.targetYear,
            targetMonth: null,
            amount: null,
            comments: '',
          });
          this.loadOneTimeEntries();
        },
        error: (err) => {
          this.addingOneTime = false;
          const message = err?.error?.reason || err?.error?.message || 'Failed to add one-time entry';
          console.error('One-time add failed', err);
          this.toaster.showError(message, 'Add failed');
        },
      }),
    );
  }

  private buildNewProjectForm(): FormGroup {
    return this.fb.group({
      projectId: [null as number | null, Validators.required],
      hiringStatus: ['', Validators.required],
      capexOpex: ['', Validators.required],
      // Derived fields for the read-only Leader & Supporting LOB display;
      // dntIndiaLeader is editable on a fresh assignment (the operator
      // chooses the leader), supportingLob auto-fills from the picker.
      dntIndiaLeader: [''],
      supportingLob: [''],
      allocationPct: [
        null as number | null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      billingStartDate: [null as string | Date | null, Validators.required],
      billingEndDate: [null as string | Date | null, Validators.required],
      // Rate-card cluster — all optional; BE only creates a rate card
      // row when at least one of these is filled in.
      baseRate: [null],
      billToFu: [''],
      billToMo: [''],
      billToBu: [''],
      // Tier Model — drives the monthly Total formula via finance_tier_rate.
      // Free text; operators typically pick from the canonical 7 (Tier 1-4,
      // Actuals, Vendor Billing, Non-Billable).
      tierModel: [''],
    });
  }

  onNewProjectChange(projectId: number | null): void {
    if (projectId == null) return;
    const project = this.projectsById.get(projectId);
    if (!project) return;
    this.newProjectForm.patchValue({
      supportingLob: project.supportingLob ?? '',
    });
  }

  composedLeaderForNew(): string {
    return this.composedLeader(this.newProjectForm.value);
  }

  addProject(): void {
    if (this.newProjectForm.invalid) {
      this.newProjectForm.markAllAsTouched();
      this.toaster.showError('Please fix the highlighted fields and try again', 'Validation error');
      return;
    }
    this.addingProject = true;
    const v = this.newProjectForm.value;
    const payload: AddResourceProjectRequest = {
      projectId: v.projectId,
      hiringStatus: v.hiringStatus,
      capexOpex: v.capexOpex,
      dntIndiaLeader: v.dntIndiaLeader || undefined,
      allocationPct: v.allocationPct ?? undefined,
      billingStartDate: this.toIsoDate(v.billingStartDate),
      billingEndDate: this.toIsoDate(v.billingEndDate) || undefined,
      baseRate: v.baseRate ?? undefined,
      billToFu: v.billToFu || undefined,
      billToMo: v.billToMo || undefined,
      billToBu: v.billToBu || undefined,
      tierModel: v.tierModel || undefined,
    };
    this.subs.add(
      this.financeService.addResourceProject(this.resourceId, payload).subscribe({
        next: () => {
          this.addingProject = false;
          this.toaster.showSuccess('Project added', 'Success');
          // Reset the New Project form, refresh Current Project tab + the
          // right side panel (a CREATE history row just landed), and flip
          // the tab so the operator sees the new assignment in the list.
          this.newProjectForm.reset();
          this.activeMainTab = 0;
          this.loadEditView();
          this.loadRightPanel();
        },
        error: (err) => {
          this.addingProject = false;
          const message = err?.error?.reason || err?.error?.message || 'Failed to add project';
          console.error('Add Project failed', err);
          this.toaster.showError(message, 'Add failed');
        },
      }),
    );
  }

  /** Material datepicker yields a Date object on the form control while
   *  the BE expects yyyy-MM-dd. Normalises before send; passes through
   *  pre-formatted strings unchanged. */
  private toIsoDate(value: string | Date | null | undefined): string {
    if (!value) return '';
    if (value instanceof Date) {
      const y = value.getFullYear();
      const m = String(value.getMonth() + 1).padStart(2, '0');
      const d = String(value.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
    return value;
  }

  get projectsArray(): FormArray {
    return this.editForm.get('projects') as FormArray;
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('resourceId'));
    if (!id || Number.isNaN(id)) {
      this.router.navigate(['/finance/details']);
      return;
    }
    this.resourceId = id;
    this.resourceName = this.route.snapshot.queryParamMap.get('name') ?? '';
    this.loadEditView();
    this.loadHiringStatusOptions();
    this.loadProjectOptions();
    this.loadTierRateMaster();
    this.loadRightPanel();
  }

  /** Loads the master tier rates for the current year so Tier Model
   *  changes on the Current / New Project forms can auto-fill Base Rate
   *  via {@link onTierModelChange}. Silent on error — the form falls
   *  back to manual Base Rate entry when the master isn't available. */
  private loadTierRateMaster(): void {
    const year = new Date().getFullYear();
    this.subs.add(
      this.financeService.listTierRates(year).subscribe({
        next: (rows) => {
          this.tierRateMaster.clear();
          for (const r of rows) {
            if (r.tierModel) this.tierRateMaster.set(r.tierModel, r);
          }
        },
        error: () => this.tierRateMaster.clear(),
      }),
    );
  }

  /** Fires from the Tier Model select on either the Current Project
   *  sections or the New Project tab. Patches Base Rate per Product's
   *  spec:
   *    - Tier = "Non-Billable"           → 0           (any POI)
   *    - POI = "EMP" + Tier 1-4          → master      (auto-fill)
   *    - POI = "EMP" + Tier = "Actuals"  → manual      (no patch)
   *    - POI = "CWR"                     → manual SOW  (no patch)
   *  Operator can override the patched value before saving. */
  onTierModelChange(target: FormGroup, tier: string | null): void {
    if (!tier) return;
    if (tier === 'Non-Billable') {
      target.patchValue({ baseRate: 0 });
      target.markAsDirty();
      return;
    }
    // EMP-only auto-fill from master. CWR / Actuals fall through to
    // manual entry — leave whatever value is in the field intact.
    if (this.poiType !== 'EMP') return;
    if (tier === 'Actuals') return;
    const master = this.tierRateMaster.get(tier);
    if (master?.baseRate == null) return;
    target.patchValue({ baseRate: master.baseRate });
    target.markAsDirty();
  }

  private loadProjectOptions(): void {
    this.subs.add(
      this.financeService.listFinanceProjects().subscribe({
        next: (opts) => {
          this.projectOptions = opts;
          this.projectsById = new Map(opts.map((p) => [p.id, p]));
        },
        error: () => {
          this.projectOptions = [];
          this.projectsById = new Map();
        },
      }),
    );
  }

  /**
   * Called when the user picks a new project from the dropdown.
   * Updates the section's projectId form control (binding does this
   * already) plus the supportingLob — which lives on the project, not
   * the assignment, so it has to refresh client-side. Leader stays
   * untouched (it's assignment-scoped).
   */
  onProjectChange(sectionIndex: number, projectId: number | null): void {
    if (projectId == null) return;
    const project = this.projectsById.get(projectId);
    if (!project) return;
    const group = this.projectsArray.at(sectionIndex);
    // Project-scoped fields refresh to the new project's defaults on
    // pick — supportingLob plus the three Bill-to fields which the
    // operator sees as read-only inputs but expects to follow the
    // project selection.
    group.patchValue({
      supportingLob: project.supportingLob ?? '',
      billToFu: project.billToFu ?? '',
      billToMo: project.billToMo ?? '',
      billToBu: project.glBusinessUnit ?? '',
    });
    group.markAsDirty();
  }

  /**
   * Composes "{leader} ({lob})" for the read-only Leader & Supporting LOB
   * display. Falls back gracefully when one or both are missing.
   */
  composedLeader(group: { dntIndiaLeader?: string; supportingLob?: string } | null): string {
    if (!group) return '';
    const leader = (group.dntIndiaLeader ?? '').trim();
    const lob = (group.supportingLob ?? '').trim();
    if (!leader && !lob) return '';
    if (!lob) return leader;
    if (!leader) return `(${lob})`;
    return `${leader} (${lob})`;
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadEditView(): void {
    this.loading = true;
    this.errorMessage = null;
    this.subs.add(
      this.financeService.loadEditProjectView(this.resourceId).subscribe({
        next: (view: EditResourceProjectView) => {
          this.employeeId = view.employeeId;
          this.poiType = view.poiType ?? null;
          this.rebuildForm(view.projects);
          this.loading = false;
        },
        error: (err) => {
          console.error('Edit Project load failed', err);
          this.errorMessage = 'Unable to load resource. Please try again.';
          this.loading = false;
          this.toaster.showError('Failed to load resource', 'Error');
        },
      }),
    );
  }

  private loadHiringStatusOptions(): void {
    this.subs.add(
      this.financeService.listReferenceEntries('hiring-status').subscribe({
        next: (opts) => (this.hiringStatusOptions = opts),
        error: () => (this.hiringStatusOptions = []),
      }),
    );
    this.subs.add(
      this.financeService.listReferenceEntries('capex-opex').subscribe({
        next: (opts) => (this.capexOpexOptions = opts),
        error: () => (this.capexOpexOptions = []),
      }),
    );
  }

  private loadRightPanel(): void {
    this.rightPanelLoading = true;
    this.subs.add(
      forkJoin({
        project: this.financeService.listProjectHistory(this.resourceId),
        recent: this.financeService.listRecentActivities(this.resourceId),
      }).subscribe({
        next: ({ project, recent }) => {
          // Both panels now include rate_card AND assignment entries —
          // the prior filter dropped rate_card history rows wholesale,
          // which hid every Tier Model / Laptop Issued / Vendor Rate Card
          // / GL Account / Bill-to-* change from the operator's audit
          // view. The Recent Activities card renders changes (From/To)
          // which works for both entity kinds; the Project History card
          // renders snapshot values for assignment-scoped fields and
          // gracefully degrades to "—" for keys a rate_card snapshot
          // doesn't carry — acceptable trade-off vs. silently hiding
          // half the audit trail.
          this.projectHistory = project;
          this.recentActivities = recent;
          this.rightPanelLoading = false;
        },
        error: () => {
          // Side panel is contextual — failing to load it shouldn't break
          // the form. Render empty + a small toast.
          this.projectHistory = [];
          this.recentActivities = [];
          this.rightPanelLoading = false;
        },
      }),
    );
  }

  /** Clears + repopulates the FormArray from a fresh view. Called on
   *  initial load and after a successful save (so the form reflects the
   *  server's post-save state). */
  private rebuildForm(sections: EditResourceProjectSection[]): void {
    while (this.projectsArray.length > 0) {
      this.projectsArray.removeAt(0);
    }
    sections.forEach((s) => this.projectsArray.push(this.buildSectionGroup(s)));
    // Material datepicker (and mat-select with object-vs-string values)
    // can write a normalised value back to the form control during its
    // initialisation pass, which marks the control dirty before the user
    // has touched anything. Reset pristine state after Angular has
    // flushed those synchronous writes so the Save button starts
    // disabled, and only enables on a real user edit.
    Promise.resolve().then(() => this.editForm.markAsPristine());
  }

  private buildSectionGroup(s: EditResourceProjectSection): FormGroup {
    return this.fb.group({
      // assignmentId + projectId + rateCardId are read-only on the form but
      // are part of the payload sent on Save — keep them in the group so
      // .value snapshots them.
      assignmentId: [s.assignmentId],
      projectId: [s.projectId ?? null],
      rateCardId: [s.rateCardId ?? null],

      // Display-only convenience fields (read-only inputs in the template).
      projectName: [s.projectName ?? ''],
      projectIdCode: [s.projectIdCode ?? ''],
      // Separate leader + lob fields so the picker can refresh just the
      // lob on project change. The template composes them for display.
      dntIndiaLeader: [s.dntIndiaLeader ?? ''],
      supportingLob: [s.supportingLob ?? ''],

      // Editable fields.
      hiringStatus: [s.hiringStatus ?? '', Validators.required],
      allocationPct: [
        s.allocationPct ?? null,
        [Validators.required, Validators.min(0), Validators.max(100)],
      ],
      billingStartDate: [s.billingStartDate ?? null, Validators.required],
      billingEndDate: [s.billingEndDate ?? null, Validators.required],
      baseRate: [s.baseRate ?? null],
      billToFu: [s.billToFu ?? ''],
      billToMo: [s.billToMo ?? ''],
      billToBu: [s.billToBu ?? ''],
      tierModel: [s.tierModel ?? ''],
      vendorRateCard: [s.vendorRateCard ?? ''],
      laptopIssued: [s.laptopIssued ?? ''],
      // glAccount is presented read-only on the form (the value is
      // auto-derived from CAPEX/OPEX on the BE). Disabled so it
      // serialises without conflicting with the form's dirty checks.
      glAccount: [{ value: s.glAccount ?? '', disabled: true }],
    });
  }

  /** Section header label: "Project 1", "Project 2", etc. */
  sectionLabel(index: number): string {
    return `Project ${index + 1}`;
  }

  save(): void {
    if (this.editForm.invalid) {
      this.editForm.markAllAsTouched();
      this.toaster.showError('Please fix the highlighted fields and try again', 'Validation error');
      return;
    }
    this.saving = true;
    const payload = {
      projects: this.projectsArray.value.map((row: EditResourceProjectSection) => ({
        assignmentId: row.assignmentId,
        projectId: row.projectId,
        rateCardId: row.rateCardId,
        hiringStatus: row.hiringStatus,
        allocationPct: row.allocationPct,
        billingStartDate: row.billingStartDate,
        billingEndDate: row.billingEndDate,
        baseRate: row.baseRate,
        billToFu: row.billToFu,
        billToMo: row.billToMo,
        billToBu: row.billToBu,
        tierModel: row.tierModel,
        vendorRateCard: row.vendorRateCard,
        laptopIssued: row.laptopIssued,
        // glAccount is intentionally NOT sent — the BE auto-derives it
        // from capex/opex on save.
      })),
    };
    this.subs.add(
      this.financeService.saveEditProjects(this.resourceId, payload).subscribe({
        next: (result) => {
          this.saving = false;
          this.toaster.showSuccess(
            `Updated — ${result.assignmentsUpdated} assignment(s), ${result.rateCardsUpdated} rate card(s)`,
            'Success',
          );
          // Refresh both the form (in case BE applied any normalization)
          // and the right-panel feeds (new history rows just landed).
          this.loadEditView();
          this.loadRightPanel();
        },
        error: (err) => {
          this.saving = false;
          const message = err?.error?.reason || err?.error?.message || 'Update failed';
          console.error('Edit Project update failed', err);
          this.toaster.showError(message, 'Update failed');
        },
      }),
    );
  }

  cancel(): void {
    this.router.navigate(['/finance/details']);
  }

  backToDetails(): void {
    this.router.navigate(['/finance/details']);
  }

  backToDashboard(): void {
    this.router.navigate(['/finance']);
  }

  // ---- Formatting helpers for the right side panel ----

  formatHistoryHeader(entry: ResourceHistoryEntry): string {
    if (!entry.changedBy) return entry.changedAt ?? '';
    const date = this.formatShortDate(entry.changedAt);
    return `${entry.changedBy} ${date}`;
  }

  /** Recent Activities header per Figma: "{user} updated on dd/MM/yy at HH:mm". */
  formatRecentHeader(entry: ResourceHistoryEntry): string {
    if (!entry.changedBy) return entry.changedAt ?? '';
    return `${entry.changedBy} updated on ${this.formatTimestamp(entry.changedAt)}`;
  }

  formatShortDate(iso?: string): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(2);
    return `${dd}/${mm}/${yy}`;
  }

  /** Pretty-print the snapshot's value for inline display. BE serialises
   *  snapshot values as primitives (string / number / date string), so we
   *  reject any object payload defensively rather than letting String()
   *  emit "[object Object]". */
  snapshotValue(value: unknown): string {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return '—';
  }
}
